/**
 * @file src/audio/effects/toneParams.js
 * @description How a value reaches a Tone node's parameter — the one copy of it.
 *
 * The rule, from the effect design standard: anything that changes CONTINUOUSLY is delivered by the
 * browser on the audio thread, and the main thread never carries audio timing. A value written as an
 * AudioParam ramp is scheduled once and then interpolated by the audio thread, so it survives a
 * stalled frame; a value written as a plain assignment lands whenever the main thread next gets a
 * turn. Under load the second kind lurches while the first stays smooth — and until now the default
 * here was the second kind.
 *
 * So `rampSeconds` defaults to a ramp rather than to zero. The cost is nil: it is the same single
 * write, at the same cadence, scheduled as a short interpolation instead of a step. What it buys is
 * that a burst of frames arriving late collapses into one ramp toward the newest value rather than a
 * volley of steps.
 *
 * Two things still want an immediate write, and they use `setToneValueImmediate`:
 *   - construction-time values, where there is no previous value to glide from;
 *   - parameters where a glide is WRONG rather than merely unnecessary — a delay line's time is the
 *     clearest case, since sliding it re-pitches whatever is still ringing in the buffer.
 *
 * Parameters that are plain JS properties rather than AudioParams (a filter bank's base frequency, a
 * reverb's decay, a waveshaper's curve order) cannot ramp at all — they fall through to assignment
 * here, and the standard's answer for them is not to modulate them live in the first place.
 */

/** Default glide for a continuous parameter write. Long enough to remove the step, short enough to
 *  stay musically immediate at the ~60-90ms cadence these are written at. */
export const DEFAULT_PARAM_RAMP_SECONDS = 0.05;

/**
 * The glide a manifest control asks for, clamped to the bounds it declares.
 *
 * An effect's manifest is where a per-parameter glide is authored — a chorus wants a longer one than
 * an EQ — so this is the one place that reads `smoothing`, and the default above is what a control
 * that declares nothing gets.
 *
 * @param {{ smoothing?: { defaultRamp?: number, minRamp?: number, maxRamp?: number } }} control
 * @returns {number} glide in seconds, always > 0
 */
export function deriveRampTime(control = {}) {
  const smoothing = control.smoothing || {};
  let ramp = Number(smoothing.defaultRamp);
  if (!Number.isFinite(ramp) || ramp <= 0) ramp = DEFAULT_PARAM_RAMP_SECONDS;
  const minRamp = Number(smoothing.minRamp);
  const maxRamp = Number(smoothing.maxRamp);
  if (Number.isFinite(minRamp) && ramp < minRamp) ramp = minRamp;
  if (Number.isFinite(maxRamp) && ramp > maxRamp) ramp = maxRamp;
  return ramp;
}

/**
 * Walk a dotted path to the object that holds the final key.
 * @param {object} target
 * @param {string|string[]} path
 * @returns {{ context: object|undefined, leaf: string|undefined }}
 */
export function resolvePath(target, path) {
  const segments = Array.isArray(path) ? path : String(path).split('.');
  const leaf = segments.pop();
  let context = target;
  for (const segment of segments) {
    if (context == null) break;
    context = context[segment];
  }
  return { context, leaf };
}

/**
 * The Tone.Param / Tone.Signal behind a path, or the plain value when there is none.
 * @param {object} node
 * @param {string|string[]} path
 */
export function getToneProperty(node, path) {
  if (!path) return null;
  const { context, leaf } = resolvePath(node, path);
  if (!context || !leaf) return null;
  const property = context[leaf];
  if (property && typeof property === 'object' && 'value' in property) {
    return property;
  }
  return property ?? null;
}

/**
 * Write `value` to `path` on `node`.
 *
 * @param {object} node
 * @param {string|string[]} path
 * @param {number} value
 * @param {number} [rampSeconds] - glide length. Anything at or below 0 (and any non-number) writes
 *   immediately. Defaults to a short ramp.
 */
export function setToneValue(node, path, value, rampSeconds = DEFAULT_PARAM_RAMP_SECONDS) {
  const { context, leaf } = resolvePath(node, path);
  if (!context || !leaf) return;
  const property = context[leaf];
  if (property && typeof property === 'object' && 'value' in property) {
    if (rampSeconds > 0 && typeof property.rampTo === 'function') {
      property.rampTo(value, rampSeconds);
    } else {
      property.value = value;
    }
  } else {
    // Not an AudioParam — a plain accessor on the node. Nothing to schedule; this one genuinely
    // lands on the main thread, which is why these are not meant to be modulated continuously.
    context[leaf] = value;
  }
}

/**
 * Write with no glide. For construction-time values, and for parameters where sliding is an artefact
 * rather than a smoothing (delay time, anything that rebuilds a buffer).
 */
export function setToneValueImmediate(node, path, value) {
  setToneValue(node, path, value, 0);
}
